import Vue from 'vue'

export default {
	namespaced: true,
	// modules: {}
	state: {
		openInfo: null,
		/**
		 * 平台資訊（由 getPlatformInfo API 取得）
		 * @type {Object|null}
		 * @property {string} [kioskUrl] - Kiosk 模式的網址
		 * @property {string} [contactusUrl] - 聯絡我們的網址
		 * @property {Object} [partner] - 合作夥伴資訊
		 * @property {boolean} [partner.enabled] - 是否啟用合作夥伴按鈕
		 * @property {string} [partner.url] - 合作夥伴連結網址
		 * @property {string} [partner.icon] - 合作夥伴圖示網址
		 */
		platformInfo: null,
		menus: null,
		refreshMenu: false, // 刷新店家菜單
		refreshSetting: false, // 刷新店家設定
		refreshAutomaticDiscount: false, // 刷新店家自動折扣
		whatsEatShopId: null, //WhatsEat綁定店家ID
		whatsEatCouponAmount: null, //WhatsEat優惠卷數量
		itemAutomaticDiscountList: [], // 商品自動折扣列表
		orderAutomaticDiscountList: [], // 訂單自動折扣列表
		automaticDiscountListReady: false, // 自動折扣列表已就緒
		// ── 版本號家族（本模組皆不持久化，重整後歸零）──────────────────────
		// 各自單一責任，勿合併：合在一起會讓「同輪共用」與「舊回應不得蓋新回應」互相干擾。
		// - menuPatchSeq：menus 被就地 patch，畫面需重打依價格蓋章的折扣標價
		// - cartConflictRevision：新一輪 compareCart 衝突
		// - fullMenuConflictRevision：當前完整菜單快照已同步到的衝突輪次
		// - menuChangeSeq：後端 MENU 異動事件
		// - fullMenuRequestSeq / appliedFullMenuRequestSeq：完整菜單請求的發出順序 / 已套用順序
		// - socket.reconnectSeq：WebSocket 成功重連（來源在 socket 模組，此處只記錄快照當下的值）
		//
		// patch 在 action 內發生、menus 引用不變，頁面 watch 不到深層變化，需靠此訊號得知
		menuPatchSeq: 0,
		// compareCart 每次發現衝突即遞增；完整菜單同步後記住當下版本，
		// 讓同一輪衝突的多筆重新選購共用一份快照。
		cartConflictRevision: 0,
		fullMenuConflictRevision: 0,
		// 完整菜單快取（getMenu(keyword: '') 的快照）取得當下的狀態，
		// 供 utils/menuCache.js 判斷「同店家返回菜單頁時可否略過 API」
		fullMenuCacheMeta: {
			storeId: null,
			fetchedAt: 0,
			menuChangeSeq: 0,
			reconnectSeq: 0,
		},
		// 後端 MENU 推播版本；無條件遞增（推播的即時補刷才看當前路由）
		menuChangeSeq: 0,
		// 完整菜單請求的全域發號器：Store 與 CartGrid 可能同時發出，
		// 用它擋掉「舊回應蓋掉新回應」
		fullMenuRequestSeq: 0,
		appliedFullMenuRequestSeq: 0,
	},
	getters: {
		storeLocation: state => ({
			address: state.openInfo.address,
			latitude: state.openInfo.latitude,
			longitude: state.openInfo.longitude,
		}),
		// 重新選購是否可沿用現有完整快照（同一輪衝突只補抓一次）。
		// 須帶當前店家：跨店殘留的 menus 不可視為新鮮
		isFullMenuFreshForCartConflicts: state => storeId =>
			Array.isArray(state.menus) &&
			storeId != null &&
			state.fullMenuCacheMeta.storeId != null &&
			String(state.fullMenuCacheMeta.storeId) === String(storeId) &&
			state.fullMenuConflictRevision === state.cartConflictRevision,
	},
	mutations: {
		SET_TAKE_OPEN_INFO($, value) {
			$.openInfo = value
		},
		SET_PLATFORM_INFO($, value) {
			$.platformInfo = value

			if (value && value.kioskUrl) {
				const currentOrigin = window.location.origin
				const isKioskDomain = currentOrigin === value.kioskUrl
				this.commit('kiosk/SET_IS_KIOSK_DOMAIN', isKioskDomain, { root: true })
				if (isKioskDomain) {
					this.commit('kiosk/SET_IS', true, { root: true })
					$devLog('當前域名與 kioskUrl 相同')
				}
			}
		},
		// 完整菜單快照的唯一入口（Store 首載 / 補刷 / 搜尋 fallback、CartGrid 重新選購共用）。
		// menus、快取資訊與衝突蓋章必須一起寫，否則會出現「資訊說很新、內容卻是舊的」。
		//
		// requestSeq 由 BUMP_FULL_MENU_REQUEST_SEQ 取號，比已套用的更舊就整筆丟棄：
		// 兩個完整請求交錯回應時，先落地的是較新的那份，舊回應不得覆蓋。
		// 呼叫端須自行比對 appliedFullMenuRequestSeq 是否等於自己的 requestSeq，
		// 被拒絕時改用當前 menus 收尾，不可把「回應被拒絕」當成商品不存在。
		SET_FULL_MENU_SNAPSHOT(
			$,
			{
				requestSeq,
				menuList,
				storeId,
				fetchedAt,
				menuChangeSeq,
				reconnectSeq,
				cartConflictRevision,
			},
		) {
			if (requestSeq < $.appliedFullMenuRequestSeq) {
				return
			}
			$.appliedFullMenuRequestSeq = requestSeq
			$.menus = menuList
			$.fullMenuCacheMeta = {
				storeId,
				fetchedAt,
				menuChangeSeq,
				reconnectSeq,
			}
			// 寫入「請求發出時」的衝突輪次：等待期間若又產生新一輪衝突，
			// 這裡寫進來的舊值與當前值對不上，重新選購就會再補抓一次
			$.fullMenuConflictRevision = cartConflictRevision
		},
		BUMP_FULL_MENU_REQUEST_SEQ($) {
			$.fullMenuRequestSeq++
		},
		BUMP_MENU_CHANGE_SEQ($) {
			$.menuChangeSeq++
		},
		// 搜尋回應是當下單品的完整資料，可覆蓋快取中的同一商品；
		// 但不代表其他商品也已同步，因此不更新 fullMenuConflictRevision。
		MERGE_MENU_ITEM($, latestItem) {
			if (!Array.isArray($.menus) || !latestItem) return
			for (const category of $.menus) {
				const index = (category.itemList || []).findIndex(
					item => String(item.itemId) === String(latestItem.itemId),
				)
				if (index !== -1) {
					Vue.set(category.itemList, index, latestItem)
					return
				}
			}
		},
		BUMP_CART_CONFLICT_REVISION($) {
			$.cartConflictRevision++
		},
		// compareCart 後把最新商品「頂層」資料同步回菜單快取。僅限白名單三欄位：
		// getItems 的巢狀結構與 getMenu 不同構（未過濾隱藏項與下架商品、
		// itemType / tag 等欄位名與型別不同），不可覆蓋。
		//
		// 三欄位一律「有值才覆蓋」：compareCart 的 availableForSale 為 null 時視同可售
		// 是「比對端」的 fail-open（不誤攔），但 patch 端若照樣寫入 null / undefined，
		// 會把菜單原本的售完標記洗掉讓商品變回可點；itemName / price 缺值同理會讓菜單顯示壞掉。
		//
		// 必須就地更新欄位（Vue.set），不可替換物件：菜單頁「全部」分類的列表是
		// 扁平化出來的新陣列、持有的是舊物件參考，替換物件會讓它看不到更新；
		// availableForSale 可能原本不存在於 getMenu 回傳，直接賦值不具響應性。
		PATCH_MENU_ITEMS($, latestItems) {
			if (!$.menus || !latestItems?.length) return
			const latestById = latestItems.reduce((acc, item) => {
				acc[item.itemId] = item
				return acc
			}, {})
			let patched = false
			$.menus.forEach(category => {
				category.itemList?.forEach(item => {
					const latest = latestById[item.itemId]
					if (!latest) return

					if (latest.itemName != null && latest.itemName !== item.itemName) {
						Vue.set(item, 'itemName', latest.itemName)
						patched = true
					}
					if (latest.price != null && latest.price !== item.price) {
						Vue.set(item, 'price', latest.price)
						patched = true
					}
					if (
						latest.availableForSale != null &&
						latest.availableForSale !== item.availableForSale
					) {
						Vue.set(item, 'availableForSale', latest.availableForSale)
						patched = true
					}
				})
			})
			if (patched) {
				$.menuPatchSeq++
			}
		},
		SET_REFRESH_SETTING($, value) {
			$.refreshSetting = value
		},
		SET_REFRESH_MENU($, value) {
			$.refreshMenu = value
		},
		SET_REFRESH_AUTOMATIC_DISCOUNT($, value) {
			$.refreshAutomaticDiscount = value
		},
		SET_WHATS_EAT_SHOP_ID($, value) {
			$.whatsEatShopId = value
		},
		SET_WHATS_EAT_COUPON_AMOUNT($, value) {
			$.whatsEatCouponAmount = value
		},
		SET_ITEM_AUTOMATIC_DISCOUNT_LIST($, value) {
			$.itemAutomaticDiscountList = value
		},
		SET_ORDER_AUTOMATIC_DISCOUNT_LIST($, value) {
			$.orderAutomaticDiscountList = value
		},
		SET_AUTOMATIC_DISCOUNT_LIST_READY($, value) {
			$.automaticDiscountListReady = value
		},
	},
	actions: {},
}
